import { useEffect, useState } from "react";
import { Circle, Monitor, Smartphone, Tablet, Clock, MapPin, LogIn, LogOut, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const deviceOf = (ua = "") => {
  const u = ua.toLowerCase();
  if (/iphone|android.*mobile|ipod/.test(u)) return "mobile";
  if (/ipad|tablet|android/.test(u)) return "tablet";
  return "desktop";
};
const DEV_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

const fmtDur = (s) => {
  if (s === null || s === undefined || s < 0) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min`;
  return "< 1min";
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function Sessions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/sessions").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  const online = rows.filter((r) => r.online);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-4xl text-forest">Connexions</h1>
        <button onClick={load} data-testid="sessions-refresh" className="inline-flex items-center gap-1.5 text-sm text-stone hover:text-forest"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Actualiser</button>
      </div>
      <p className="text-stone mb-6">Suivi des connexions à l'outil · <span className="font-semibold text-green-600">{online.length} en ligne</span> maintenant</p>

      {online.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="online-now">
          {online.map((r) => {
            const Icon = DEV_ICON[deviceOf(r.user_agent)] || Monitor;
            return (
              <div key={r.id} className="rounded-xl bg-white border border-green-200 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Circle size={9} className="fill-green-400 text-green-400 animate-pulse" />
                  <p className="font-medium text-forest text-sm">{r.name}</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand/10 text-brand capitalize ml-auto">{r.role}</span>
                </div>
                <p className="text-xs text-stone flex items-center gap-1.5"><Icon size={13} /> Depuis {fmt(r.login_at)} · {fmtDur(r.duration_seconds)}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="rounded-xl bg-white border border-border overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr_0.8fr_1fr] gap-3 px-5 py-3 bg-[#F7F7F8] text-xs font-semibold text-stone uppercase tracking-wide">
          <span>Membre</span><span>Connexion</span><span>Déconnexion</span><span>Durée</span><span>Appareil / IP</span>
        </div>
        {rows.length === 0 ? <p className="text-stone text-sm p-10 text-center">Aucune connexion enregistrée.</p> : (
          <div className="divide-y divide-border">
            {rows.map((r) => {
              const Icon = DEV_ICON[deviceOf(r.user_agent)] || Monitor;
              return (
                <div key={r.id} data-testid={`session-${r.id}`} className="grid md:grid-cols-[1.4fr_1fr_1fr_0.8fr_1fr] gap-1 md:gap-3 px-5 py-3 items-center text-sm">
                  <div className="flex items-center gap-2">
                    <Circle size={9} className={r.online ? "fill-green-400 text-green-400" : "fill-stone/40 text-stone/40"} />
                    <div className="min-w-0"><p className="font-medium text-forest truncate">{r.name}</p><p className="text-xs text-stone truncate capitalize">{r.role}</p></div>
                  </div>
                  <span className="text-stone flex items-center gap-1.5"><LogIn size={13} className="text-green-600 shrink-0" /> {fmt(r.login_at)}</span>
                  <span className="text-stone flex items-center gap-1.5">{r.online ? <span className="text-green-600 font-medium">En ligne</span> : <><LogOut size={13} className="text-stone shrink-0" /> {fmt(r.logout_at || r.last_seen)}</>}</span>
                  <span className="text-forest flex items-center gap-1.5"><Clock size={13} className="text-brand shrink-0" /> {fmtDur(r.duration_seconds)}</span>
                  <span className="text-xs text-stone flex items-center gap-1.5"><Icon size={14} className="shrink-0" /> {r.ip || "—"}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
